import React, { useState, useEffect } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { Dialog, Button, Dropdown, TextInput, Image } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import { useGetGeneralParameters } from "@hooks/useGeneralParams";
import CKEditorText from "@components/CKEditorText/CKEditorText";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";
import {
  labelStyles,
  inputStyles,
  labelStylesError,
  inputStylesError,
} from "@utils/extra-style";
import {
  HeaderWrapper,
  Title,
  ButtonWrapper,
  FormBody,
  OptionsWrapper,
  FlexRow,
  SectionContainer,
  GridRow,
  RequiredMessage,
  ContainerLabel,
} from "./AddEditSpecificCondition.styled";
import closeIcon from "@icons/ic-close-white.svg";

const AddEditSpecificCondition = (props) => {
  const {
    t,
    id,
    isEditMode,
    title,
    onClose,
    updateRefeshStatus,
    selectedSpecificCondition,
  } = props;
  const [isSpecificConditionCreating, setIsSpecificConditionCreating] =
    useState(false);
  const [createSpecificConditionAPIError, setCreateSpecificConditionAPIError] =
    useState("");
  const [descriptionData, setDescriptionData] = useState(
    selectedSpecificCondition?.content ?? ""
  );

  const [parentList, setParentList] = useState([]);

  const generalParams = useGetGeneralParameters();

  const [formData, setFormData] = useState({
    country: {
      required: true,
      value: selectedSpecificCondition?.country?.id ?? "",
      error: "",
    },
    parent: {
      required: true,
      value: selectedSpecificCondition?.parent?.id ?? "",
      error: "",
    },
    isTitle: {
      required: true,
      value: selectedSpecificCondition?.isTitle ?? false,
      error: "",
    },
    label: {
      required: true,
      value: selectedSpecificCondition?.label ?? "",
      error: "",
    },
    title: {
      required: true,
      value: selectedSpecificCondition?.title ?? "",
      error: "",
    },
    parameters: {
      required: true,
      value: selectedSpecificCondition?.parameters ?? "",
      error: "",
    },
    content: {
      required: true,
      value: selectedSpecificCondition?.content ?? true,
      error: "",
    },
    isActive: {
      required: true,
      value: selectedSpecificCondition?.isActive ?? true,
      error: "",
    },
  });

  const fetchParents = (country) => {
    axios
      .get(
        `/general/specificConditions?isTitle=1&countries=[${country}]&orderBy=asc&limit=-1`
      )
      .then((response) => {
        let specificConditionData = response.data.specificCondition;
        if (isEditMode) {
          specificConditionData = specificConditionData.filter(
            (data) => parseInt(id, 10) !== parseInt(data.id, 10)
          );
        }
        const conditionData = specificConditionData.map((condition) => {
          return {
            label: condition.label,
            value: condition.id,
          };
        });

        setParentList(conditionData);
      })
      .catch((error) => {});
  };

  const onParentChange = (e) => {
    setFormData({
      ...formData,
      parent: {
        value: e.value,
        error: "",
        required: true,
      },
    });
  };

  useEffect(() => {
    if (formData.country.value !== "") {
      fetchParents(formData.country.value);
    }
  }, [formData.country.value]);

  useEffect(() => {
    setFormData({
      ...formData,
      content: {
        value: descriptionData,
        error: "",
        required: true,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [descriptionData]);

  const onCountryChange = (e) => {
    setFormData({
      ...formData,
      country: {
        value: e.value,
        error: "",
        required: true,
      },
      parent: {
        value: "",
        error: "",
        required: true,
      },
    });
  };

  const onLabelChange = (e) => {
    setFormData({
      ...formData,
      label: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };
  const onTitleChange = (e) => {
    setFormData({
      ...formData,
      title: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const onParameterChange = (e) => {
    setFormData({
      ...formData,
      parameters: {
        value: e.target.value,
        error: "",
        required: true,
      },
    });
  };

  const validateForm = (data) => {
    let isFormValid = true;
    if (!data.country.value) {
      isFormValid = false;
      data.country.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!data.label.value) {
      isFormValid = false;
      data.label.error = t("GENERAL_MANDATORY_ERROR");
    }
    if (!isFormValid) {
      setFormData({ ...data });
    }

    return isFormValid;
  };

  const getFormData = () => {
    const data = {
      id: isEditMode ? selectedSpecificCondition.id : "",
      country: JSON.stringify({
        id: formData.country.value,
      }),
      parent: formData.parent.value,
      isTitle: formData.isTitle.value,
      label: formData.label.value,
      title: formData.title.value,
      content: formData.content.value,
      parameters: formData.parameters.value,
      isActive: formData.isActive.value,
    };
    return data;
  };

  const onSubmit = () => {
    const isFormValid = validateForm(formData);
    if (isFormValid) {
      setIsSpecificConditionCreating(true);
      setCreateSpecificConditionAPIError("");
      // Create solution
      if (!id && !isEditMode) {
        axios
          .post("/general/specificConditions", getFormData())
          .then(() => {
            setIsSpecificConditionCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsSpecificConditionCreating(false);
            setCreateSpecificConditionAPIError(
              error.response?.data?.message ?? ""
            );
          });
      }
      if (id && isEditMode) {
        axios
          .put(`/general/specificConditions/${id}`, getFormData())
          .then(() => {
            setIsSpecificConditionCreating(false);
            updateRefeshStatus();
            onClose();
          })
          .catch((error) => {
            setIsSpecificConditionCreating(false);
            setCreateSpecificConditionAPIError(
              error.response?.data?.message ?? ""
            );
          });
      }
    }
  };

  return (
    <Dialog>
      <div>
        <HeaderWrapper>
          <Title>
            {title} {id ? "#" + id : ""}
          </Title>
          <Button
            onClick={onClose}
            styles={{
              backgroundColor: "transparent",
              border: "none",
              height: "auto",
              padding: 0,
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            <Image src={closeIcon} alt="close Product price popup" />
          </Button>
        </HeaderWrapper>
        <FormBody>
          <OptionsWrapper>
            <FlexRow>
              <SectionContainer>
                <div>
                  <GridRow column="1fr">
                    <Dropdown
                      id="payterm-term-country"
                      label={t("COUNTRY")}
                      name="country"
                      value={formData.country.value}
                      onChange={onCountryChange}
                      defaultOption={t("COUNTRY")}
                      options={generalParams.countryList}
                      labelStyles={
                        formData.country.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.country.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      required
                      error={formData.country.error}
                    />
                  </GridRow>
                  <GridRow column="60% 1fr">
                    <Dropdown
                      id="specific-condition-parent"
                      label={t("SPECIFIC_CONDITION_PARENT")}
                      placeholder={t("PRODUCT")}
                      name="parent"
                      value={formData.parent.value}
                      onChange={onParentChange}
                      defaultOption={t("SPECIFIC_CONDITION_PARENT")}
                      options={parentList}
                      labelStyles={
                        formData.parent.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.parent.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.parent.error}
                    />

                    <div style={{ marginTop: "1.5rem" }}>
                      <Image
                        src={formData.isTitle.value ? enableIcon : disableIcon}
                        alt={t("SPECIFIC_CONDITION_IS_TITLE")}
                        title={t("SPECIFIC_CONDITION_IS_TITLE")}
                        styles={{ width: "auto" }}
                        onClick={() => {
                          setFormData({
                            ...formData,
                            isTitle: {
                              value: !formData.isTitle.value,
                              error: "",
                              required: true,
                            },
                          });
                        }}
                      />
                      <label className="label center-products-list__active-label">
                        {t("SPECIFIC_CONDITION_IS_TITLE")}
                      </label>
                    </div>
                  </GridRow>

                  <GridRow column="1fr">
                    <TextInput
                      id="label"
                      type="text"
                      label={t("LABEL")}
                      isFloat
                      required
                      value={String(formData.label.value)}
                      onChange={(e) => onLabelChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.label.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.label.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.label.error}
                      placeholder={t("LABEL")}
                    />
                  </GridRow>
                  <GridRow column="1fr">
                    <TextInput
                      id="title"
                      type="text"
                      label={t("SPECIFIC_CONDITION_TITLE")}
                      isFloat
                      value={String(formData.title.value)}
                      onChange={(e) => onTitleChange(e)}
                      containerStyles={{ marginTop: "21px" }}
                      errorStyles={{ "font-style": "italic" }}
                      labelStyles={
                        formData.title.error !== ""
                          ? labelStylesError
                          : labelStyles
                      }
                      inputStyles={
                        formData.title.error !== ""
                          ? inputStylesError
                          : inputStyles
                      }
                      error={formData.title.error}
                      placeholder={t("SPECIFIC_CONDITION_TITLE")}
                    />
                  </GridRow>
                  <GridRow column="50% 1fr">
                    <div>
                      <ContainerLabel htmlFor="description">
                        {t("SPECIFIC_CONDITION_CONTENT")}
                      </ContainerLabel>
                      <CKEditorText
                        descriptionData={descriptionData}
                        setDescriptionData={setDescriptionData}
                      />
                      {formData.content.error && (
                        <RequiredMessage>
                          {formData.content.error}
                        </RequiredMessage>
                      )}
                    </div>
                    <div>
                      <TextInput
                        type="textarea"
                        label={t("SPECIFIC_CONDITION_PARAMETERS")}
                        isFloat
                        id="parameters"
                        name="parameters"
                        value={formData.parameters.value}
                        onChange={onParameterChange}
                        containerStyles={{ marginTop: "25px" }}
                        inputStyles={{
                          padding: "7px 5px 7px 10px",
                          height: "13rem",
                          marginTop: "10px",
                        }}
                      />
                    </div>
                  </GridRow>
                </div>
              </SectionContainer>
            </FlexRow>
          </OptionsWrapper>
        </FormBody>

        <ButtonWrapper>
          {createSpecificConditionAPIError && (
            <div>{createSpecificConditionAPIError}</div>
          )}
          <Button
            styles={{
              color: "white",
              background: "#1BACE4",
              border: "1px solid #1BACE4",
              marginRight: "0.32rem",
              "&:hover": {
                backgroundColor: "#1589b6",
              },
            }}
            onClick={onSubmit}
          >
            <>
              {isSpecificConditionCreating && (
                <ClipLoader size={15} color="#fff" />
              )}
              {!isSpecificConditionCreating && t("SUBMIT")}
            </>
          </Button>

          <Button
            styles={{
              color: "#333",
              background: "#fff",
              border: "1px solid #ccc",
              "&:hover": {
                backgroundColor: "#ccc",
              },
            }}
            onClick={onClose}
          >
            {t("CLOSE")}
          </Button>
        </ButtonWrapper>
      </div>
    </Dialog>
  );
};

AddEditSpecificCondition.propTypes = {
  t: PropTypes.func.isRequired,
  id: PropTypes.number,
  title: PropTypes.string.isRequired,
  isEditMode: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  updateRefeshStatus: PropTypes.func.isRequired,
};

AddEditSpecificCondition.defaultProps = {
  id: null,
  isEditMode: false,
};
export default withTranslation()(AddEditSpecificCondition);
